<template>
  <div v-if="userHasPermissions('provider.specialist.license.view')">
    <section-title content="Información de Licencia(s)" :has-line="true" />
    <p
      v-if="activateSpecialistLicenseFilterByHealthDepartment"
      class="text-gray"
    >
      <em class="far fa-info-circle mr-1" />
      Las licencias correspondientes al Departamento de Salud, deben ser
      agregadas mediante la acción Consultar DS
    </p>
    <div
      v-if="licensesData && licensesData.length > 0"
      class="license-information-list mt-3"
    >
      <template v-for="(license, idx) in orderedLinceses">
        <callout
          v-if="(idx >= pageStartIndex) & (idx < pageEndIndex)"
          :key="license.specialistLicenseId || license.tempId"
          class="mb-2"
          :has-toggle="false"
          :opened="!disabled && editMode"
        >
          <div class="row mt-2 mb-2">
            <div class="col-sm-12 col-md-2 cell">
              <div class="data">
                <span>Especialidad:</span>
                <p>{{ license.specialtyTypeName }}</p>
              </div>
            </div>
            <div class="col-sm-12 col-md cell">
              <div v-if="hasDetails(license.specialtyTypeId)" class="data">
                <span>Número de Licencia:</span>
                <p>{{ license.licenseNo }}</p>
              </div>
            </div>
            <div class="col-sm-12 col-md cell">
              <div v-if="hasDetails(license.specialtyTypeId)" class="data">
                <span>Fecha de Expiración:</span>
                <p :class="getClassExpiration(license.licenseExpireDate)">
                  {{ license.licenseExpireDate | date }}
                </p>
              </div>
            </div>
            <div
              v-if="hasDetails(license.specialtyTypeId)"
              class="col-sm-12 col-md cell"
            >
              <div class="data">
                <span>Tipo de Licencia:</span>
                <p>
                  {{ license.specialtyTypeNameHd ? 'SALUD' : 'MIPE' }}
                </p>
              </div>
            </div>
            <div
              v-if="hasDetails(license.specialtyTypeId)"
              class="col-sm-12 col-md cell"
            >
              <div class="data">
                <span>Estatus:</span>
                <p>
                  {{
                    license.specialtyTypeNameHd && !license.isFromHd
                      ? 'Pendiente'
                      : 'Validada'
                  }}
                </p>
              </div>
            </div>
            <div class="col-sm-12 col-md cell">
              <div v-if="license.supervisorFlag" class="data d-inline-block">
                <custom-balloon
                  background-color="gray"
                  class="mt-2 pl-2 pr-2 font-weight-normal font-16"
                  text="Supervisor"
                />
              </div>
              <div v-if="license.provisionalFlag" class="data d-inline-block">
                <custom-balloon
                  background-color="gray"
                  class="mt-2 pl-2 pr-2 font-weight-normal font-16"
                  text="Provisional"
                />
              </div>
            </div>
            <div
              v-if="!disabled && canPerformActions"
              class="col-sm-12 col-md mr-md-4 d-flex justify-content-end align-items-center actions"
            >
              <drop-down
                class="float-right"
                :data-item="idx"
                :items="getAvailableActions()"
              />
            </div>
          </div>

          <template v-if="editMode && editIndex == idx" #edition>
            <license-information-add-edit
              v-model="licensesData[idx]"
              :custom-error-message="addEditCustomErrorMessage"
              :immediate-save="immediateSave"
              :is-medicaid-documentation-workflow-enabled="
                isMedicaidDocumentationWorkflowEnabled
              "
              :is-related-master="isRelatedMaster"
              op-mode="E"
              :selected-licenses="licensesData"
              :specialist-id="specialistId"
              :specialist-license-document-types="
                specialistLicenseDocumentTypes
              "
              @on-cancel="clearEditCreate"
              @on-saved="emitLicenseChange"
            />
          </template>
        </callout>
      </template>
      <div v-if="!disabled" class="row mt-3">
        <div class="col">
          <action-button
            v-if="!editMode && canAddLicense"
            class="float-right"
            i="fas fa-file-plus"
            variant="success"
            @click="onAdd"
          >
            Añadir
          </action-button>
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <pagination
            v-if="this.pagingInfo.totalItemCount > 5"
            class="mb-3"
            :data="this.pagingInfo"
            :show-text="true"
            @pagination-go-page="setCurrentPage"
          />
        </div>
      </div>
    </div>
    <div v-else class="license-information-list mt-3">
      <callout
        v-if="
          licensesData.length == 0 &&
            canAddLicense &&
            !createMode &&
            !editMode &&
            !disabled
        "
        class-main="py-3"
        :has-toggle="false"
      >
        <div class="row d-flex justify-content-center">
          <action-button
            class="border w-40 btn"
            i="fas fa-file-plus color-primary-1"
            text="Presione aquí para añadir Licencia(s)"
            @click="onAdd"
          />
        </div>
      </callout>
    </div>

    <callout
      class="mb-2"
      :opened="!disabled && createMode"
      :show-container="false"
    >
      <template #edition>
        <license-information-add-edit
          v-model="newLicense"
          :activate-specialist-license-filter-by-health-department="
            activateSpecialistLicenseFilterByHealthDepartment
          "
          :custom-error-message="addEditCustomErrorMessage"
          :immediate-save="immediateSave"
          :is-a-t-evaluator="isATEvaluator"
          :is-csee-official="isCseeOfficial"
          :is-medicaid-documentation-workflow-enabled="
            isMedicaidDocumentationWorkflowEnabled
          "
          :is-related-master="isRelatedMaster"
          op-mode="I"
          :selected-licenses="licensesData"
          :specialist-company-id="specialistCompanyId"
          :specialist-id="specialistId"
          :specialist-license-document-types="specialistLicenseDocumentTypes"
          @on-cancel="clearEditCreate"
          @on-saved="onLicenseAdded"
        />
      </template>
    </callout>
  </div>
</template>

<script>
import SectionTitle from '@/shared/components/SectionTitle';
import Pagination from '@/components/common/Pagination';
import LicenseInformationAddEdit from './LicenseInformationAddEdit';
import { deleteLicense } from '@/modules/provider/api';
import CustomBalloon from '@/components/common/CustomBalloon';
import SpecialtyTypes from '@/utils/constants/specialtyTypes.js';
import alert from '@/mixins/alert.mixin';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';

export default {
  name: 'LicenseInformationList',
  components: {
    LicenseInformationAddEdit,
    SectionTitle,
    Pagination,
    CustomBalloon,
  },
  mixins: [alert],
  model: {
    prop: 'licenses',
    event: 'on-license-change',
  },
  props: {
    licenses: {
      type: Array,
      required: false,
      default: () => [],
    },
    isRelatedMaster: {
      type: Boolean,
      default: false,
    },
    isCseeOfficial: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    specialistId: {
      type: Number,
      default: () => null,
    },
    isATEvaluator: {
      type: [Object, Boolean],
      default: () => false,
    },
    onDeleting: {
      type: Function,
      default: null,
    },
    addEnabled: {
      type: Boolean,
      default: true,
    },
    editEnabled: {
      type: Boolean,
      default: true,
    },
    deleteEnabled: {
      type: Boolean,
      default: true,
    },
    immediateSave: {
      type: Boolean,
      required: true,
    },
    specialistLicenseDocumentTypes: {
      type: Array,
      required: false,
      default: () => [],
    },
    specialistCompanyId: {
      type: Number,
      default: () => null,
    },
    isMedicaidDocumentationWorkflowEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      licensesData: [],
      editMode: false,
      editIndex: null,
      createMode: false,
      newLicense: {},
      activateSpecialistLicenseFilterByHealthDepartment: false,
      pagingInfo: {
        totalItemCount: 0,
        count: 5,
        pageCount: 0,
        currentPage: 1,
      },
      addEditCustomErrorMessage: null,
    };
  },
  computed: {
    pageStartIndex() {
      return this.pagingInfo.count * (this.pagingInfo.currentPage - 1);
    },
    pageEndIndex() {
      return (
        this.pagingInfo.count * (this.pagingInfo.currentPage - 1) +
        this.pagingInfo.count
      );
    },
    canAddLicense() {
      return this.addEnabled;
    },
    canPerformActions() {
      return this.editEnabled || this.deleteEnabled;
    },

    orderedLinceses() {
      return (this.licenses || [])
        .sort((a, b) => a.specialtyTypeName.localeCompare(b.specialtyTypeName))
        .sort(
          (a, b) =>
            new Date(b.licenseExpireDate) - new Date(a.licenseExpireDate)
        );
    },
  },
  watch: {
    licenses: function(newValue) {
      this.licensesData = newValue;
      // Refresh de UI when the page information changes (add/delete)
      let pageCount = Math.ceil(newValue.length / this.pagingInfo.count);

      if (this.pagingInfo.currentPage <= pageCount) {
        this.setCurrentPage(this.pagingInfo.currentPage);
      } else {
        this.setCurrentPage(this.pagingInfo.currentPage - 1);
      }
    },
    createMode: function(newValue) {
      if (newValue) {
        this.$emit('on-opened');
      }
    },
    editMode: function(newValue) {
      if (newValue) {
        this.$emit('on-opened');
      }
    },
  },
  async created() {
    const { data } = await _getSysparameterByNames({
      parameterNames: ['ActivateSpecialistLicenseFilterByHealthDepartment'],
    });
    this.activateSpecialistLicenseFilterByHealthDepartment =
      data?.length > 0 && data[0].staticValue?.toLowerCase() === 'true';
  },
  methods: {
    setCurrentPage(pageNumber) {
      pageNumber = pageNumber < 1 ? 1 : pageNumber;
      this.pagingInfo.currentPage = pageNumber;
      this.pagingInfo.totalItemCount = this.licensesData.length;
      this.pagingInfo.pageCount = Math.ceil(
        this.licensesData.length / this.pagingInfo.count
      );
    },
    hasDetails(id) {
      return (
        id != SpecialtyTypes.ServicioEducativo &&
        id != SpecialtyTypes.AsistenteServicio &&
        id != SpecialtyTypes.FuncionarioDEPR
      );
    },
    getClassExpiration(date) {
      return new Date(date).getTime() < new Date().getTime()
        ? 'color-primary-4'
        : 'color-primary-1';
    },
    getAvailableActions() {
      let actions = [
        {
          name: 'edit',
          label: 'Editar',
          icon: 'fas fa-edit color-primary-1',
          click: this.onEdit,
          visible: !this.disabled && this.editEnabled,
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-danger',
          click: this.onDelete,
          visible: !this.disabled && this.licensesData.length > 1,
        },
      ];

      return actions;
    },
    onAdd() {
      this.createMode = true;
      this.editMode = false;
      this.newLicense = this.getNewLicenseModel();
    },
    onEdit(dataItemIndex) {
      this.editMode = true;
      this.createMode = false;
      this.editIndex = dataItemIndex;
    },
    async onDelete(dataItemIndex) {
      var self = this;
      await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea eliminar la especialidad ' +
          self.licensesData[dataItemIndex].specialtyTypeName +
          '?',
        'Sí',
        'No'
      ).then(async (answer) => {
        if (answer) {
          if (this.immediateSave) {
            deleteLicense(self.licensesData[dataItemIndex].specialistLicenseId)
              .then(async () => {
                self.deleteFromModel(dataItemIndex, self);
                self.emitLicenseChange();
                await this.ShowToast(
                  ' Confirmación',
                  'La especialidad ha sido eliminada exitosamente.',
                  'success'
                );
              })
              .catch((error) => {
                self.ShowToast('Error', error.response.data.message, 'error');
              });
          } else {
            self.deleteFromModel(dataItemIndex, self);
            self.emitLicenseChange();
          }
        }
      });
    },
    deleteFromModel(index, self) {
      self.licensesData.splice(index, 1);
    },
    emitLicenseChange() {
      this.clearEditCreate();
      this.$emit('on-license-change', this.licensesData);
    },
    onLicenseAdded() {
      this.licensesData.push(this.newLicense);
      this.emitLicenseChange();
      this.clearEditCreate();
    },
    clearEditCreate() {
      this.createMode = false;
      this.editMode = false;
    },
    getNewLicenseModel() {
      return {
        specialistLicenseId: null,
        specialistId: this.specialistId,
        specialtyTypeId: 0,
        specialtyTypeName: '',
        licenseNo: null,
        licenseExpireDate: null,
        ageRangeCd: 0,
        ageRangeName: '',
        provisionalFlag: null,
        supervisorFlag: false,
        isCVE: null,
        isICVE: null,
        isCEV: null,
        tempId: this.immediateSave ? null : this.getTempId(),
      };
    },
    getTempId() {
      let tempIds = this.licensesData.map((l) => l.tempId);
      let minId = tempIds.length > 0 ? Math.min(...tempIds) : 0;
      return minId - 1;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';

.license-information-list .cell {
  display: table-cell !important;
  vertical-align: middle;
  margin-left: 2.2rem;
}

.license-information-list .cell span {
  color: map-get($colors, 'gray-6');
  font-size: map-get($fontsSize, 'font-14');
}

.license-information-list .cell p {
  color: map-get($colors, 'black');
  font-size: map-get($fontsSize, 'font-16');
  margin-bottom: 0;
}

.license-information-list .cell p:last-child {
  font-weight: 500;
}

.license-information-list .cell {
  margin-top: auto;
  margin-bottom: auto;
  padding-left: 0px;
  padding-right: 0px;
}

.license-information-list .cell.sd {
  margin-top: 4.5px;
}

.license-information-list .cell.actions {
  text-align: center;
}
.dot {
  background-color: #ff7058;
  box-shadow: 0px 3px 6px #00000029;
  margin: 2px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  border-color: #fff;
  display: inline-block;
}
.is-big-icon {
  margin-left: 3px;
}
.color-white {
  color: white;
}

@media (max-width: 768px) {
  .license-information-list .cell.actions {
    text-align: left !important;
  }

  .license-information-list .cell p:last-child {
    margin-top: 0;
    margin-bottom: 10px;
  }
}

@media (max-width: 992px) {
  .license-information-list .cell.hidden {
    display: none !important;
  }
}

@media (min-width: 992px) and (max-width: 1145px) {
  .license-information-list .cell.hidden {
    display: none !important;
  }
}
</style>
